import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { DollarSign, ShoppingBag, TrendingUp, XCircle, TrendingDown, Percent, AlertTriangle } from 'lucide-react'
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import PeriodFilter, { type Period } from '../components/PeriodFilter'

const PIE_COLORS = ['#C41E3A', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6']

interface SalesOverview {
  revenue_fils: number
  revenue_delta: number | null
  order_count: number
  order_delta: number | null
  avg_order_value_fils: number
  cancellation_rate: number
  cancelled_orders: number
  // Optional COGS/margin fields — populated when the endpoint is called
  // with `?include=cogs`. Zero if no PO receives have run yet (products
  // have no cost basis).
  cogs_fils?: number
  gross_margin_fils?: number
  gross_margin_pct?: number | null
  // Shrinkage = value of stock lost to count corrections (Phase 12.2)
  // in the current period. Non-zero means the count found less on the
  // shelf than the system said. Waste movements (once that flow ships)
  // join the same bucket.
  shrinkage_fils?: number
  // Raw units-lost count. Needed to distinguish "no corrections in
  // period" (qty === 0) from "corrections happened but the affected
  // products had no cost basis so value came out zero" (qty > 0, fils === 0).
  shrinkage_qty?: number
}

interface TimeseriesPoint { day: string; revenue_fils: number; order_count: number }
interface TopProduct { id: string; name_en: string; image_key: string | null; units_sold: number; revenue_fils: number }
interface CategoryRow {
  id: string; name_en: string; order_count: number; units_sold: number; revenue_fils: number
  cogs_fils?: number; margin_fils?: number; margin_pct?: number | null
}
interface PaymentRow { payment_method: string; order_count: number; revenue_fils: number }
interface HourPoint { hour: number; order_count: number }

const PAYMENT_LABELS: Record<string, string> = {
  cod: 'Cash on Delivery',
  card: 'Card',
  cliq: 'CliQ',
  card_on_delivery: 'Card on Delivery',
}

type Channel = 'all' | 'delivery' | 'pos'

export default function Sales() {
  const [period, setPeriod] = useState<Period>('7d')
  // Channel scope — orders unification (migration 0104) put POS sales into
  // the same `orders` table as delivery rows. The default `all` reproduces
  // the pre-POS behaviour; switching to `delivery` or `pos` filters every
  // tile + chart on this page to that channel.
  const [channel, setChannel] = useState<Channel>('all')
  const params = `period=${period}${channel === 'all' ? '' : `&channel=${channel}`}`
  const paramsCogs = `${params}&include=cogs`

  const overview   = useQuery<SalesOverview>({ queryKey: ['sales-overview', period, channel], queryFn: () => api.get(`/analytics/sales-overview?${paramsCogs}`).then(r => r.data) })
  const timeseries = useQuery<{ series: TimeseriesPoint[] }>({ queryKey: ['sales-timeseries', period, channel], queryFn: () => api.get(`/analytics/revenue-timeseries?${params}`).then(r => r.data) })
  const topProds   = useQuery<{ products: TopProduct[] }>({ queryKey: ['top-products', period, channel], queryFn: () => api.get(`/analytics/top-products?${params}&limit=10`).then(r => r.data) })
  const categories = useQuery<{ categories: CategoryRow[] }>({ queryKey: ['category-perf', period, channel], queryFn: () => api.get(`/analytics/category-performance?${paramsCogs}`).then(r => r.data) })
  const payments   = useQuery<{ methods: PaymentRow[] }>({ queryKey: ['payment-methods', period, channel], queryFn: () => api.get(`/analytics/payment-methods?${params}`).then(r => r.data) })
  const hours      = useQuery<{ series: HourPoint[] }>({ queryKey: ['orders-by-hour', period, channel], queryFn: () => api.get(`/analytics/orders-by-hour?${params}`).then(r => r.data) })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white">Sales & Products</h1>
        <div className="flex items-center gap-3">
          <ChannelFilter value={channel} onChange={setChannel} />
          <PeriodFilter value={period} onChange={setPeriod} />
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
        <Kpi icon={<DollarSign size={18} />} label="Revenue" value={formatCurrency(overview.data?.revenue_fils ?? 0)} delta={overview.data?.revenue_delta} />
        <Kpi icon={<ShoppingBag size={18} />} label="Orders" value={`${overview.data?.order_count ?? 0}`} delta={overview.data?.order_delta} />
        <Kpi icon={<TrendingUp size={18} />} label="Avg Order Value" value={formatCurrency(overview.data?.avg_order_value_fils ?? 0)} />
        <Kpi icon={<XCircle size={18} />} label="Cancellation Rate" value={`${((overview.data?.cancellation_rate ?? 0) * 100).toFixed(1)}%`} sublabel={`${overview.data?.cancelled_orders ?? 0} cancelled`} />
      </div>
      {/* Margin + shrinkage tiles — populated once PO receives have run
          so products have a cost basis. Shrinkage populates once a
          stock count has been posted (Phase 12.2). */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Kpi
          icon={<TrendingDown size={18} />}
          label="Cost of Goods Sold"
          value={formatCurrency(overview.data?.cogs_fils ?? 0)}
          sublabel={(overview.data?.cogs_fils ?? 0) === 0 ? 'No PO receives yet — cost basis is zero' : undefined}
        />
        <Kpi
          icon={<DollarSign size={18} />}
          label="Gross Margin"
          value={formatCurrency(overview.data?.gross_margin_fils ?? 0)}
        />
        <Kpi
          icon={<Percent size={18} />}
          label="Gross Margin %"
          value={overview.data?.gross_margin_pct != null ? `${(overview.data.gross_margin_pct * 100).toFixed(1)}%` : '—'}
          sublabel={overview.data?.gross_margin_pct == null ? 'Needs revenue + COGS' : undefined}
        />
        <Kpi
          icon={<AlertTriangle size={18} />}
          label="Shrinkage"
          value={formatCurrency(overview.data?.shrinkage_fils ?? 0)}
          sublabel={(() => {
            const fils = overview.data?.shrinkage_fils ?? 0
            const qty = overview.data?.shrinkage_qty ?? 0
            if (qty === 0) return 'No count corrections in period'
            if (fils === 0) return `${qty} units short — receive stock via a PO to unlock cost basis`
            return `${qty} units short`
          })()}
        />
      </div>

      {/* Revenue chart */}
      <Card title="Revenue trend">
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeseries.data?.series ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2e2e2e" />
              <XAxis dataKey="day" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}`} />
              <Tooltip
                contentStyle={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 8 }}
                labelStyle={{ color: '#fff' }}
                formatter={(v, name) => name === 'revenue_fils' ? [formatCurrency(Number(v)), 'Revenue'] : [String(v), 'Orders']}
              />
              <Line type="monotone" dataKey="revenue_fils" stroke="#C41E3A" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {/* Top products */}
        <Card title="Top products">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-[10px] uppercase tracking-wide">
                <th className="text-left pb-2 font-semibold">Product</th>
                <th className="text-right pb-2 font-semibold">Units</th>
                <th className="text-right pb-2 font-semibold">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(topProds.data?.products ?? []).map(p => (
                <tr key={p.id} className="border-t border-[#2e2e2e]">
                  <td className="py-2 text-white truncate max-w-xs">{p.name_en}</td>
                  <td className="py-2 text-right text-gray-300 tabular-nums">{p.units_sold}</td>
                  <td className="py-2 text-right text-white tabular-nums font-semibold">{formatCurrency(p.revenue_fils)}</td>
                </tr>
              ))}
              {topProds.data?.products?.length === 0 && (
                <tr><td colSpan={3} className="py-6 text-center text-gray-500 text-xs">No data in this period.</td></tr>
              )}
            </tbody>
          </table>
        </Card>

        {/* Category performance — chart on top, table below so the cashier-
            facing breakdown (units sold + orders touched + revenue per
            category) is readable as a report, not just a bar chart. */}
        <Card title="Sales by category">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categories.data?.categories ?? []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#2e2e2e" horizontal={false} />
                <XAxis type="number" stroke="#9CA3AF" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}`} />
                <YAxis dataKey="name_en" type="category" stroke="#9CA3AF" fontSize={11} width={90} />
                <Tooltip
                  contentStyle={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 8 }}
                  formatter={(v) => [formatCurrency(Number(v)), 'Revenue']}
                />
                <Bar dataKey="revenue_fils" fill="#C41E3A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 max-h-56 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#1a1a1a]">
                <tr className="text-gray-500 text-[10px] uppercase tracking-wide">
                  <th className="text-left pb-2 font-semibold">Category</th>
                  <th className="text-right pb-2 font-semibold">Orders</th>
                  <th className="text-right pb-2 font-semibold">Units</th>
                  <th className="text-right pb-2 font-semibold">Revenue</th>
                  <th className="text-right pb-2 font-semibold">Margin</th>
                  <th className="text-right pb-2 font-semibold">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {(categories.data?.categories ?? []).map(c => (
                  <tr key={c.id} className="border-t border-[#2e2e2e]">
                    <td className="py-1.5 text-white truncate max-w-[160px]">{c.name_en}</td>
                    <td className="py-1.5 text-right text-gray-300 tabular-nums">{c.order_count}</td>
                    <td className="py-1.5 text-right text-gray-300 tabular-nums">{c.units_sold}</td>
                    <td className="py-1.5 text-right text-white tabular-nums font-semibold">{formatCurrency(c.revenue_fils)}</td>
                    <td className="py-1.5 text-right text-white tabular-nums">{formatCurrency(c.margin_fils ?? 0)}</td>
                    <td className="py-1.5 text-right text-gray-300 tabular-nums">{c.margin_pct != null ? `${(c.margin_pct * 100).toFixed(0)}%` : '—'}</td>
                  </tr>
                ))}
                {categories.data?.categories?.length === 0 && (
                  <tr><td colSpan={4} className="py-6 text-center text-gray-500 text-xs">No data in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Payment methods */}
        <Card title="Payment methods">
          <div className="h-64 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={payments.data?.methods ?? []}
                  dataKey="revenue_fils"
                  nameKey="payment_method"
                  cx="50%" cy="50%"
                  outerRadius={80}
                  label={(entry: any) => entry.payment_method ? (PAYMENT_LABELS[entry.payment_method as string] ?? entry.payment_method) : ''}
                  labelLine={false}
                >
                  {(payments.data?.methods ?? []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 8 }}
                  formatter={(v) => [formatCurrency(Number(v)), 'Revenue']}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: '#9CA3AF' }}
                  formatter={(value) => PAYMENT_LABELS[value] ?? value} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Orders by hour */}
        <Card title="Orders by hour (Amman time)">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hours.data?.series ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2e2e2e" />
                <XAxis dataKey="hour" stroke="#9CA3AF" fontSize={11} tickFormatter={(h) => `${h}:00`} />
                <YAxis stroke="#9CA3AF" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: 8 }}
                  labelFormatter={(h) => `${h}:00 – ${h}:59`}
                  formatter={(v) => [String(v), 'Orders']}
                />
                <Bar dataKey="order_count" fill="#C41E3A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  )
}

function Kpi({ icon, label, value, delta, sublabel }: {
  icon: React.ReactNode; label: string; value: string; delta?: number | null; sublabel?: string
}) {
  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-xs">{label}</span>
        <span className="text-[#C41E3A]">{icon}</span>
      </div>
      <p className="text-xl font-bold text-white tabular-nums">{value}</p>
      {sublabel && <p className="text-[10px] text-gray-500 mt-1">{sublabel}</p>}
      {delta !== null && delta !== undefined && (
        <div className={`flex items-center gap-1 text-[10px] mt-1 ${delta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {delta >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
          <span className="tabular-nums">{(Math.abs(delta) * 100).toFixed(1)}% vs prev</span>
        </div>
      )}
    </div>
  )
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white/[0.02] border border-[#2e2e2e] rounded-lg p-4">
      <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">{title}</h3>
      {children}
    </div>
  )
}

// Channel selector — All / Delivery / POS. Matches the visual treatment of
// PeriodFilter so the two sit next to each other in the page header.
function ChannelFilter({ value, onChange }: { value: Channel; onChange: (c: Channel) => void }) {
  const opts: { id: Channel; label: string }[] = [
    { id: 'all',      label: 'All' },
    { id: 'delivery', label: 'Delivery' },
    { id: 'pos',      label: 'In-store' },
  ]
  return (
    <div className="inline-flex rounded-lg border border-[#2e2e2e] overflow-hidden">
      {opts.map(o => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={`px-3 py-1.5 text-xs font-semibold ${
            value === o.id
              ? 'bg-[#C41E3A] text-white'
              : 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
